/**
 * Module valuation-comparison defines schemas and types for comparison valuation
 * parameters and calculated market and mortgage-lending values.
 *
 * Comparable observations and their sources are defined in valuation-comparables.ts.
 */
import { z } from 'zod';
import { MonetaryValueSchema } from './money';
import { RoundingStrategySchema } from './valuation-primitives';
const ComparisonValueScopeSchema = z.enum(['unit', 'gross_floor_area', 'living_area', 'usage_area']);
const ComparisonValueSchema = z.object({
    value: MonetaryValueSchema,
    scope: ComparisonValueScopeSchema,
});
const ComparisonStructureParameterSchema = z.object({
    comparison_value: ComparisonValueSchema,
    description_text: z.string(),
    security_discount: z.number(),
});
export const ComparisonValuationParametersSchema = z.object({
    structure_parameters: ComparisonStructureParameterSchema.array(),
    rounding_strategy: RoundingStrategySchema,
    market_user_defined_value: MonetaryValueSchema.optional(),
    mortgage_user_defined_value: MonetaryValueSchema.optional(),
});
const MarketStructureValueSchema = z.object({
    value: MonetaryValueSchema,
    preliminary_value: MonetaryValueSchema,
});
const MortgageStructureValueSchema = z.object({
    preliminary_value: MonetaryValueSchema,
    value: MonetaryValueSchema,
    security_discount_value: MonetaryValueSchema,
});
const ComparisonValuationSchema = z.object({
    structure_value: MonetaryValueSchema,
    asset_value_non_rounded: MonetaryValueSchema,
    asset_value_rounded: MonetaryValueSchema,
    structure_values: MarketStructureValueSchema.array(),
    rounding_strategy: RoundingStrategySchema,
    user_defined_value: MonetaryValueSchema.optional(),
    special_property_features_value: MonetaryValueSchema,
});
const MortgageComparisonValuationSchema = z.object({
    structure_value: MonetaryValueSchema,
    preliminary_asset_value: MonetaryValueSchema,
    security_discount: MonetaryValueSchema,
    asset_value_non_rounded: MonetaryValueSchema,
    asset_value_rounded: MonetaryValueSchema,
    structure_values: MortgageStructureValueSchema.array(),
    rounding_strategy: RoundingStrategySchema,
    user_defined_value: MonetaryValueSchema.optional(),
    special_property_features_value: MonetaryValueSchema,
});
export const CombinedComparisonValuationSchema = z.object({
    market: ComparisonValuationSchema,
    mortgage: MortgageComparisonValuationSchema,
});
